/**
 * Módulo Vuex para gestionar las variables de configuración de runtime.
 * 
 * Estas variables se inyectan en el contenedor via ConfigMaps de Kubernetes
 * y se cargan desde window.process.env en el arranque de la aplicación.
 * 
 * En desarrollo local, webpack inyecta los valores via DefinePlugin.
 * En producción (contenedor), el script 20-env-subst.sh genera env-config.js
 * que define window.process.env con los valores del ConfigMap.
 */

/**
 * Obtiene una variable de entorno, priorizando runtime sobre build-time
 * @param {string} name - Nombre de la variable
 * @param {string} buildTimeValue - Valor inyectado por webpack en build-time
 * @param {string} defaultValue - Valor por defecto si no existe
 * @returns {string}
 */
function getEnvVar(name, buildTimeValue, defaultValue = '') {
  // 1. Primero intentar runtime (window.process.env del contenedor)
  if (typeof window !== 'undefined' &&
      window.process?.env?.[name] !== undefined &&
      window.process.env[name] !== '' &&
      window.process.env[name] !== '${' + name + '}') {
    return window.process.env[name]
  }

  // 2. Luego intentar build-time (valor inyectado por webpack DefinePlugin)
  if (buildTimeValue !== undefined && buildTimeValue !== '') {
    return buildTimeValue
  }

  // 3. Valor por defecto
  return defaultValue
}

// Valores por defecto para local
const defaults = {
  API_HOST: 'http://localhost:3000',
  WS_HOST: 'ws://localhost:3000/websocket',
  MAP_ENGINE: 'osm',
  OSM_URL: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  API_SUPORT_DATA: '',
  API_KEY: '',
  GOOGLE_MAPS_API_KEY: ''
}

export default {
  namespaced: true,

  state: {
    // Las variables globales (API_HOST, WS_HOST, etc.) son inyectadas por webpack DefinePlugin
    // Si no existen (ej: en tests), usamos los valores por defecto
    apiHost: getEnvVar('API_HOST', typeof API_HOST !== 'undefined' ? API_HOST : '', defaults.API_HOST),
    wsHost: getEnvVar('WS_HOST', typeof WS_HOST !== 'undefined' ? WS_HOST : '', defaults.WS_HOST),
    mapEngine: getEnvVar('MAP_ENGINE', typeof MAP_ENGINE !== 'undefined' ? MAP_ENGINE : '', defaults.MAP_ENGINE),
    osmUrl: getEnvVar('OSM_URL', typeof OSM_URL !== 'undefined' ? OSM_URL : '', defaults.OSM_URL),
    googleMapsApiKey: getEnvVar('GOOGLE_MAPS_API_KEY', typeof GOOGLE_MAPS_API_KEY !== 'undefined' ? GOOGLE_MAPS_API_KEY : '', defaults.GOOGLE_MAPS_API_KEY),
    apiSuportData: getEnvVar('API_SUPORT_DATA', typeof API_SUPORT_DATA !== 'undefined' ? API_SUPORT_DATA : '', ''),
    apiKey: getEnvVar('API_KEY', typeof API_KEY !== 'undefined' ? API_KEY : '', ''),
  },

  getters: {
    apiHost: state => state.apiHost,
    wsHost: state => state.wsHost,
    mapEngine: state => state.mapEngine,
    osmUrl: state => state.osmUrl,
    googleMapsApiKey: state => state.googleMapsApiKey,
    apiSuportData: state => state.apiSuportData,
    apiKey: state => state.apiKey,
    // Getter de compatibilidad con el módulo map existente
    osmAttribution: () => "Map data &copy; <a href='https://www.openstreetmap.org/'>OpenStreetMap</a> contributors, <a href='https://creativecommons.org/licenses/by-sa/2.0/'>CC-BY-SA</a>" // eslint-disable-line
  },

  mutations: {
    // Mutaciones para actualizar los valores si es necesario (ej: hot reload en desarrollo)
    setApiHost(state, value) {
      state.apiHost = value
    },
    setWsHost(state, value) {
      state.wsHost = value
    },
    setMapEngine(state, value) {
      state.mapEngine = value
    },
    setOsmUrl(state, value) {
      state.osmUrl = value
    },
    selftApiSuportData(state, value) {
      state.apiSuportData = value
    },
    setApiKey(state, value) {
      state.apiKey = value
    },
    setGoogleMapsApiKey(state, value) {
      state.googleMapsApiKey = value
    }
  }
}
